import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Radio } from "lucide-react";
import heroPoster from "@/assets/media/andrew-hero-poster.webp";
import heroVideo from "@/assets/media/andrew-hero.mp4";
import brandIcon from "@/assets/icons/favicon-48.webp";

const PORTRAIT = heroPoster;

const GLYPHS = "01<>/\\|_—+×#@$%&";

function Scramble({ text, delay = 0 }) {
  const [display, setDisplay] = useState(() => text.replace(/./g, " "));

  useEffect(() => {
    let interval;
    const timeout = setTimeout(() => {
      let frame = 0;
      interval = setInterval(() => {
        frame += 1;
        const settled = Math.floor(frame / 2);
        let out = "";
        for (let i = 0; i < text.length; i++) {
          if (i < settled || text[i] === " ") out += text[i];
          else out += GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        }
        setDisplay(out);
        if (settled >= text.length) clearInterval(interval);
      }, 45);
    }, delay);
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [text, delay]);

  return <>{display}</>;
}

export default function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
    layoutEffect: false,
  });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.3]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    if (document.readyState === "complete") {
      setShouldLoadVideo(true);
      return undefined;
    }
    const onLoad = () => setShouldLoadVideo(true);
    window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (v && shouldLoadVideo) {
      v.load();
      v.play().catch(() => {});
    }
  }, [shouldLoadVideo]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-screen overflow-hidden">
      <motion.div style={{ scale: bgScale, y: bgY }} className="absolute inset-0">
        <video
          ref={videoRef}
          data-testid="hero-video"
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={PORTRAIT}
          aria-hidden="true"
          tabIndex={-1}
        >
          {shouldLoadVideo && <source src={heroVideo} type="video/mp4" />}
        </video>
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-void/85 via-void/45 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-void/45 to-transparent" />
      </motion.div>

      <header className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 py-5 sm:px-12">
        <span data-testid="hero-brand" className="flex items-center gap-3 font-mono text-xs font-bold uppercase tracking-[0.3em] text-neon">
          <img
            src={brandIcon}
            width="40"
            height="40"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            className="h-10 w-10 rounded-sm border border-neon/40 object-cover"
          />
          <span>A/ATM-13</span>
        </span>
        <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ash">
          <Radio className="h-3.5 w-3.5 animate-pulse text-crimson" />
          Live transmission · The Bay
        </span>
      </header>

      <motion.div
        style={{ y: titleY, opacity: fade }}
        className="absolute inset-x-0 bottom-0 z-10 px-6 pb-24 text-center sm:px-12"
      >
        <h2
          data-testid="hero-kicker"
          className="mb-6 font-mono text-xs font-bold uppercase tracking-[0.35em] text-neon sm:text-sm"
        >
          {"13-Part Mini-Series 'Andrew Meets the machines' | A darkly humorous satire based in the Bay | Somewhat Humorous, Yet 100% Accurate"}
        </h2>
        <h1
          aria-label="Andrew Meets the machines"
          className="font-display font-black uppercase leading-[0.95] tracking-tighter"
        >
          <span className="relative mx-auto block w-fit overflow-hidden text-5xl sm:text-7xl lg:text-8xl">
            <span className="block">Andrew Meets</span>
            <motion.div
              aria-hidden
              initial={{ x: "-101%" }}
              animate={{ x: "101%" }}
              transition={{ duration: 0.9, delay: 0.55, ease: [0.7, 0, 0.3, 1] }}
              className="absolute inset-0 bg-crimson"
            />
          </span>
          <span className="relative mx-auto block w-fit overflow-hidden text-5xl text-neon sm:text-7xl lg:text-8xl">
            <span
              data-testid="hero-title-accent"
              data-text="the machines"
              className="glitch block text-glow-cyan"
            >
              <span aria-hidden className="invisible">the machines</span>
              <span className="absolute inset-0">
                <Scramble text="the machines" delay={1250} />
              </span>
            </span>
            <motion.div
              aria-hidden
              initial={{ x: "-101%" }}
              animate={{ x: "101%" }}
              transition={{ duration: 0.9, delay: 1.05, ease: [0.7, 0, 0.3, 1] }}
              className="absolute inset-0 bg-neon"
            />
          </span>
        </h1>
        <div className="mt-10 flex flex-col items-center gap-8">
          <p
            data-testid="hero-subtitle"
            className="max-w-xl border border-white/15 bg-black/55 px-6 py-4 font-mono text-sm font-normal leading-relaxed text-[#EAEAEA] backdrop-blur-sm sm:text-base"
          >
            My life after several medical devices were illegally inserted into my body —
            without my consent. <span className="text-crimson">Somewhat humorous.</span>{" "}
            <span className="text-neon">100% accurate.</span>
          </p>
          <a
            href="#synopsis"
            data-testid="hero-cta-button"
            className="cta-pulse group inline-flex items-center gap-3 border border-neon bg-black/40 px-10 py-4 font-mono text-sm font-bold uppercase tracking-[0.25em] text-neon backdrop-blur-sm transition-colors duration-200 hover:bg-neon hover:text-black"
          >
            Begin the Transmission
            <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-1" />
          </a>
        </div>
      </motion.div>
    </section>
  );
}
